import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import PinInput from 'react-pin-input';

import useTranslation from 'hooks/use-translation';
import useErnie from 'shared/hooks/use-ernie';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useAccountSignupAnalytics } from 'src/hooks/use-account-signup-analytics';
import { useSendLoginCode, useVerifyLoginCode } from 'src/state/actions/users';
import { SmallLoader } from 'shared/components';

type VerifyLoginCodeResult = {
  success: boolean;
  isNewUser?: boolean;
  user?: unknown;
  error?: {
    message?: string;
    graphQLErrors?: Array<{
      extensions?: {
        errors?: Array<{
          detail?: string;
        }>;
      };
    }>;
  };
};

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

export type EmailCodeVerifyErrorCode = 'INVALID_CODE' | 'VERIFY_REQUEST_FAILED';

export type CodeVerificationFormProps = {
  email: string;
  onSuccess: (isNewUser: boolean) => void;
  onBack: () => void;
  publicContextSessionId?: string | null;
  dispensaryId?: string;
  onVerifyFailed?: (errorCode: EmailCodeVerifyErrorCode) => void;
};

/**
 * Code verification form component for the second step of email code login.
 * User enters the 6-digit verification code sent to their email.
 */
export function CodeVerificationForm({
  email,
  onSuccess,
  onBack,
  publicContextSessionId,
  dispensaryId,
  onVerifyFailed,
}: CodeVerificationFormProps): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [codeError, setCodeError] = useState<string | null>(null);
  const pinInputRef = useRef<PinInput | null>(null);
  const { t } = useTranslation();
  const { dispensary } = useDispensary();
  const showErnie = useErnie();
  const sendLoginCode = useSendLoginCode();
  const verifyLoginCode = useVerifyLoginCode();
  const { trackSubmitted, trackSucceeded, trackFailed } = useAccountSignupAnalytics({
    surface: 'emailCode',
    method: 'emailCode',
  });
  // verifyLoginCode creates the account when it confirms isNewUser; once that terminal success
  // is tracked, later verify callbacks in this session must not emit further signup events.
  const hasSucceededRef = useRef(false);

  const notifyVerifyFailed = (errorCode: EmailCodeVerifyErrorCode): void => {
    try {
      onVerifyFailed?.(errorCode);
    } catch (err) {
      // consumer callbacks must never affect the login flow
      console.error(err);
    }
  };

  // Handle resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resendCooldown]);

  /**
   * Handles code verification when user completes entering 6 digits
   */
  const handleCodeComplete = async (code: string): Promise<void> => {
    if (code.length !== CODE_LENGTH) {
      return;
    }

    if (!hasSucceededRef.current) {
      trackSubmitted();
    }

    try {
      setLoading(true);
      setCodeError(null);

      const result = (await verifyLoginCode({
        email,
        code,
        dispensaryId: dispensaryId ?? dispensary?.id,
        publicContextSessionId,
      })) as VerifyLoginCodeResult;

      if (result.success) {
        if (result.isNewUser && !hasSucceededRef.current) {
          hasSucceededRef.current = true;
          trackSucceeded();
        }
        // Only show success message for returning users.
        // New users will see a message after completing their profile.
        if (!result.isNewUser) {
          showErnie(t('emailCodeLogin.loginSuccess', "You've been successfully logged in."), 'success');
        }
        onSuccess(result.isNewUser ?? false);
      } else {
        if (!hasSucceededRef.current) {
          trackFailed({ errorCode: 'INVALID_CODE' });
          notifyVerifyFailed('INVALID_CODE');
        }
        const errorMessage = t('emailCodeLogin.invalidCode', 'Invalid code. Please try again.');

        setCodeError(errorMessage);
        // Clear the PIN input so user can re-enter
        pinInputRef.current?.clear();
      }
    } catch (err) {
      if (!hasSucceededRef.current) {
        trackFailed({ errorCode: 'VERIFY_REQUEST_FAILED' });
        notifyVerifyFailed('VERIFY_REQUEST_FAILED');
      }
      console.error(err);
      setCodeError(t('emailCodeLogin.verifyError', 'Something went wrong. Please try again.'));
      pinInputRef.current?.clear();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles resending the verification code
   */
  const handleResendCode = async (): Promise<void> => {
    if (resendCooldown > 0) {
      return;
    }

    try {
      setLoading(true);
      setCodeError(null);

      const result = await sendLoginCode({
        email,
        dispensaryId: dispensaryId ?? dispensary?.id,
        turnstileToken: '', // Turnstile not required for resend
      });

      if (result.success) {
        showErnie(t('emailCodeLogin.codeResent', 'A new code has been sent to your email.'), 'success');
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        pinInputRef.current?.clear();
      } else {
        showErnie(t('emailCodeLogin.resendError', 'Unable to resend code. Please try again.'), 'danger');
      }
    } catch (err) {
      console.error(err);
      showErnie(t('emailCodeLogin.genericError', 'Something went wrong. Please try again.'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Mask the email for display (e.g., "t***@example.com")
  const maskedEmail = email.replace(
    /^(.{1,2})(.*)(@.*)$/,
    (_: string, start: string, middle: string, end: string) => `${start}${'*'.repeat(Math.min(middle.length, 5))}${end}`
  );

  return (
    <Container>
      <Description>{maskedEmail}</Description>

      <PinInputContainer>
        <StyledPinInput
          length={CODE_LENGTH}
          focus
          type='numeric'
          inputMode='numeric'
          autoComplete='one-time-code'
          ref={pinInputRef}
          onComplete={handleCodeComplete}
          disabled={loading}
          inputStyle={{
            borderRadius: '6px',
            fontSize: '20px',
            color: '#4f5d68',
            fontWeight: 'bold',
            height: '50px',
            width: '45px',
            marginLeft: '4px',
            marginRight: '4px',
            border: codeError ? '1px solid #E53935' : '1px solid #BCCAD2',
            backgroundColor: codeError ? '#FFF5F5' : '#FFFFFF',
          }}
          inputFocusStyle={{
            border: '1px solid #4C94D7',
            boxShadow: '0px 1px 6px rgba(11, 153, 230, 0.4)',
          }}
        />
      </PinInputContainer>

      {loading && (
        <LoaderContainer>
          <SmallLoader height={20} color='#0b99e6' />
        </LoaderContainer>
      )}

      {codeError && <ErrorMessage data-testid='code-verification-error'>{codeError}</ErrorMessage>}

      <ActionsContainer>
        <ResendButton
          type='button'
          onClick={handleResendCode}
          disabled={resendCooldown > 0 || loading}
          data-testid='code-verification-resend-button'
        >
          {resendCooldown > 0
            ? t('emailCodeLogin.resendIn', 'Resend code in {{seconds}}s', { seconds: resendCooldown })
            : t('emailCodeLogin.resendCode', "Didn't receive the code? Resend")}
        </ResendButton>

        <BackButton type='button' onClick={onBack} disabled={loading} data-testid='code-verification-back-button'>
          {t('emailCodeLogin.changeEmail', '← Change email')}
        </BackButton>
      </ActionsContainer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.grey[30]};
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 24px;
  margin-top: 0;
  text-align: center;
`;

const PinInputContainer = styled.div`
  margin-bottom: 16px;
`;

const StyledPinInput = styled(PinInput)`
  display: flex;
  justify-content: center;
`;

const LoaderContainer = styled.div`
  margin: 8px 0;
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.red?.[55] || '#E53935'};
  font-size: 14px;
  font-weight: 600;
  margin: 8px 0 16px;
  text-align: center;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  width: 100%;
`;

const ResendButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 0;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.grey[50]};
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.grey[40]};
  cursor: pointer;
  font-size: 14px;
  padding: 0;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.grey[30]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export default CodeVerificationForm;
